import {CreateAccessor, FuncExtensions} from "mobx-graphlink";
import {Vec3} from "../../../Utils/ThreeJS/VectorStructs.js";
import {MapObject} from "./@MapObject.js";
import {GetMapObject, GetMapObjects} from "../mapObjects.js";
import {OrderKey} from "../../../Utils/General/OrderKey.js";
import {DreamScene, GetDreamSceneForJournalPath, GetDreamTitle} from "../journalEntries/$journalEntry.js";
import {Mod_TerrainMaterials} from "./@MapObject/ObjModifiers/Mod_TerrainMaterials.js";
import {Mod_TerrainSceneries} from "./@MapObject/ObjModifiers/Mod_TerrainSceneries.js";
import type {MaterialRecipe} from "../../../Terrain/Materials/@MaterialTypes.js";

export const GetObjAncestors = CreateAccessor((obj: MapObject): MapObject[]=>{
	if (obj.parent == null) return [];
	const parent = GetMapObject(obj.parent, obj.map);
	if (parent == null) return [];
	return [...GetObjAncestors(parent), parent];
});

export const GetObjDescendants = CreateAccessor((obj: MapObject, keepOnlyDescendantsWithChildren = false): MapObject[]=>{
	/*const children = GetMapObjects(obj.map, obj.id);
	return [...children, ...children.flatMap(child=>GetObjDescendants(child))];*/
	const allObjects = GetMapObjects(obj.map);
	const descendants: MapObject[] = [];
	const toCheckForChildren = [obj];
	while (toCheckForChildren.length) {
		const current = toCheckForChildren.pop()!;
		const children = allObjects.filter(a=>a.parent == current.id);
		descendants.push(...children);
		toCheckForChildren.push(...children);
	}
	// useful for the alt-click "expand tree" in map-ui
	if (keepOnlyDescendantsWithChildren) {
		return descendants.filter(descendant=>allObjects.some(a=>a.parent == descendant.id));
	}
	return descendants;
});

/** All enabled `Mod_TerrainMaterials` modifiers map-wide (not just this object's); matches
 *  TerrainRenderer's scan so region assignments can reference recipes on any mod. */
export const GetTerrainMaterialsMods = CreateAccessor((mapID: string): Mod_TerrainMaterials[]=>{
	const mods: Mod_TerrainMaterials[] = [];
	for (const obj of GetMapObjects(mapID)) {
		for (const mod of obj.modifiers ?? []) {
			if (mod.enabled && mod.terrainMaterials) mods.push(mod.terrainMaterials);
		}
	}
	return mods;
});

/** All enabled `Mod_TerrainSceneries` modifiers map-wide; matches TerrainRenderer's scan so the
 *  scenery kind catalogue + budget resolve from any mod. Set13. */
export const GetTerrainSceneriesMods = CreateAccessor((mapID: string): Mod_TerrainSceneries[]=>{
	const mods: Mod_TerrainSceneries[] = [];
	for (const obj of GetMapObjects(mapID)) {
		for (const mod of obj.modifiers ?? []) {
			if (mod.enabled && mod.terrainSceneries) mods.push(mod.terrainSceneries);
		}
	}
	return mods;
});

export const GetHighestLexoRankUnderParent = CreateAccessor((mapID: string, parentID: string)=>{
	const childObjects = GetMapObjects(mapID, parentID);
	const children_lastOrderKey = childObjects.OrderBy(a=>a.orderKey).LastOrX()?.orderKey ?? OrderKey.mid().key;
	return new OrderKey(children_lastOrderKey);
});

/** Hidden if itself hidden (`hideSelf`/`hideSubtree`) or any ancestor has `hideSubtree`.
 *  `objects` is the full sibling list to walk up the parent chain (cheap for typical counts). */
export function IsMapObjectHidden(obj: MapObject, objects: ReadonlyArray<MapObject>): boolean {
	if (obj.hideSelf || obj.hideSubtree) return true;
	let currentParent = objects.find(a=>a.id == obj.parent);
	while (currentParent) {
		if (currentParent.hideSubtree) return true;
		currentParent = objects.find(a=>a.id == currentParent!.parent);
	}
	return false;
}

export const GetDisplayTitleForObj = CreateAccessor((obj: MapObject, forUI: "mapSurface" | "outliner"): string=>{
	let textFromName = obj.name;

	if (textFromName == "@" && (forUI == "mapSurface" || forUI == "outliner")) {
		const linkedDreams = (obj.journalSegmentPaths ?? []).map(a=>GetDreamSceneForJournalPath(a));
		const firstLinkedAndValidDream = linkedDreams.find(a=>a != null) as DreamScene;
		const titleToUse = firstLinkedAndValidDream ? GetDreamTitle(firstLinkedAndValidDream.segment, firstLinkedAndValidDream.sceneIndex ?? 0) : null;
		if (titleToUse) {
			textFromName = forUI == "mapSurface" ? titleToUse : `@ [${titleToUse}]`;
		}
	}

	let text = textFromName;
	if (obj.label_textOverride) {
		if (forUI == "mapSurface") text = obj.label_textOverride;
		else if (forUI == "outliner" && textFromName == "@") text = `${obj.label_textOverride.split("\n")[0]} [...]`;
	}
	return text;
});

export type AncestorPosOverride = {objId: string, position_fieldOverride: Vec3};
export const ResolveObjPosInWorld = CreateAccessor((obj: MapObject|n /*, ancestorPosOverride?: AncestorPosOverride|n*/): Vec3|n=>{
	if (obj == null) return null;
	// loop through the ancestors, applying their positions (and rotations!) to the result, in order to determine the final world position
	// (keep in mind we're using a coordinate system for Vec3 where x+ goes right, y+ goes forward, and z+ goes up)
	const result = new Vec3(obj.position.x, obj.position.y, obj.position.z);
	const ancestors = GetObjAncestors(obj).Reversed(); // process our direct parent first, then our parent's parent, etc.
	for (const ancestor of ancestors) {
		//const ancestorPos = ancestorPosOverride && ancestorPosOverride.objId == ancestor.id ? ancestorPosOverride.position_fieldOverride : ancestor.position;
		const ancestorPos = ancestor.position;
		// Convert degrees to radians, and negate for clockwise rotation (since standard math rotation is counter-clockwise)
		const ancestorRotRadians = -(ancestor.rotation ?? 0) * (Math.PI / 180);

		// apply rotation around z-axis (since z+ goes up, we rotate in the x-y plane)
		const cos = Math.cos(ancestorRotRadians);
		const sin = Math.sin(ancestorRotRadians);
		const rotatedX = result.x * cos - result.y * sin;
		const rotatedY = result.x * sin + result.y * cos;

		// then apply translation
		result.x = rotatedX + ancestorPos.x;
		result.y = rotatedY + ancestorPos.y;
		result.z += (ancestorPos.z ?? 0);
	}
	return result;
}) as {
	(obj: MapObject): Vec3;
	(obj: MapObject|null|undefined): Vec3|null;
} & FuncExtensions<{
	(obj: MapObject): Vec3;
	(obj: MapObject|null|undefined): Vec3|null;
}>;
// variant, that starts from the depth-0 object, and works its way down to the target object [this variant also works]
/*export const ResolveObjPosInWorld = CreateAccessor((obj: MapObject|n): Vec3|n=>{
	if (obj == null) return null;
	const ancestorsPlusSelf = [...GetObjAncestors(obj), obj];

	const result = Vec3.zero;
	let nextRotToApply = 0;
	for (const ancOrObj of ancestorsPlusSelf) {
		const pos = ancOrObj.position;
		const rotToApply_radians = -(nextRotToApply ?? 0) * (Math.PI / 180);

		const cos = Math.cos(rotToApply_radians);
		const sin = Math.sin(rotToApply_radians);
		const rotatedX = pos.x * cos - pos.y * sin;
		const rotatedY = pos.x * sin + pos.y * cos;

		result.x += rotatedX;
		result.y += rotatedY;
		result.z += (pos.z ?? 0);

		nextRotToApply = ancOrObj.rotation ?? 0;
	}
	return result;
}) as {
	(obj: MapObject): Vec3;
	(obj: MapObject|null|undefined): Vec3|null;
} & FuncExtensions<{
	(obj: MapObject): Vec3;
	(obj: MapObject|null|undefined): Vec3|null;
}>;*/

export const ResolveObjRotInWorld = CreateAccessor((obj: MapObject|n): number|n=>{
	if (obj == null) return null;
	let result = obj.rotation ?? 0;
	const ancestors = GetObjAncestors(obj);
	for (const ancestor of ancestors) {
		result += ancestor.rotation ?? 0;
	}
	return result;
}) as {
	(obj: MapObject): number;
	(obj: MapObject|null|undefined): number|null;
} & FuncExtensions<{
	(obj: MapObject): number;
	(obj: MapObject|null|undefined): number|null;
}>;